import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import dayjs from 'dayjs';
import styled from 'styled-components';

import ConfirmModal from 'common/Modal/ConfirmModal';
import { useRootContext } from 'components/layout/RootContext';
import { CommentPayloadType, CommentType } from 'types/comments';

import AddComment from './AddComment';
import CommentEdit from './CommentEdit';
import { getCommentActorLabel } from './helpers';
import ListComment from './ListComment';
import EditDataModal from '../../common/edit/EditDataModal';
import Header from '../../common/header/Header';

const COMMENTS_QUERY_KEY_PART = 'comments';
const COMMENTS_TITLE = 'Comments';
const COMMENT_SUBTITLE_SEPARATOR = ' - ';

const Container = styled.div`
  position: relative;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

type Props = {
  entityId?: number;
  title?: string;
  queryKeyScope: string;
  currentUserId?: number;
  currentUserAvatarUrl?: string | null;
  currentUserName?: string | null;
  dataTestIdPrefix: string;
  getComments: (entityId: number) => Promise<CommentType[]>;
  postComment: (entityId: number, payload: CommentPayloadType) => Promise<CommentType>;
  updateComment: (entityId: number, commentId: number, payload: CommentPayloadType) => Promise<CommentType>;
  deleteComment: (entityId: number, commentId: number) => Promise<unknown>;
};

const CommentsSection = ({
  entityId,
  title = COMMENTS_TITLE,
  queryKeyScope,
  currentUserId,
  currentUserAvatarUrl,
  currentUserName,
  dataTestIdPrefix,
  getComments,
  postComment,
  updateComment,
  deleteComment,
}: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const addCommentRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [editingComment, setEditingComment] = useState<CommentType | null>(null);
  const [commentToDelete, setCommentToDelete] = useState<CommentType | null>(null);

  const handleFocus = () => {
    setIsExpanded(true);
    requestAnimationFrame(() => {
      addCommentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleCancel = () => {
    setComment('');
    setIsExpanded(false);
  };

  const handleEditSuccess = () => {
    setEditingComment(null);
    queryClient.invalidateQueries([queryKeyScope, COMMENTS_QUERY_KEY_PART, entityId, currentUserId]);
  };

  const { data: comments = [] } = useQuery<CommentType[]>({
    queryKey: [queryKeyScope, COMMENTS_QUERY_KEY_PART, entityId, currentUserId],
    queryFn: () => getComments(Number(entityId)),
    enabled: Boolean(entityId),
    refetchOnWindowFocus: false,
  });

  const { mutate: createComment, isLoading: isPosting } = useMutation({
    mutationFn: () => postComment(Number(entityId), { comment }),
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeyScope, COMMENTS_QUERY_KEY_PART, entityId, currentUserId]);
      setComment('');
      setIsExpanded(false);
    },
  });

  const handlePost = () => {
    if (!comment.trim() || !entityId || isPosting) {
      return;
    }

    createComment();
  };

  const { mutate: removeComment, isLoading: isDeleting } = useMutation({
    mutationFn: (commentId: number) => deleteComment(Number(entityId), commentId),
    onSuccess: () => {
      queryClient.invalidateQueries([queryKeyScope, COMMENTS_QUERY_KEY_PART, entityId, currentUserId]);
      setCommentToDelete(null);
      showMessage('success', 'Comment deleted');
    },
    onError: () => {
      showMessage('error', 'Failed to delete comment');
    },
  });

  const handleDeleteConfirm = () => {
    if (!commentToDelete?.id) {
      return;
    }

    removeComment(commentToDelete.id);
  };

  const getCommentSubtitle = (selectedComment: CommentType | null) =>
    selectedComment?.created_by && selectedComment?.created_at
      ? `by ${getCommentActorLabel(selectedComment)}${COMMENT_SUBTITLE_SEPARATOR}${dayjs(
          selectedComment.created_at
        ).format('MMM DD YYYY')}`
      : '';

  return (
    <Container data-test-id={dataTestIdPrefix}>
      <Header title={title} />
      <Content>
        <div ref={addCommentRef}>
          <AddComment
            avatarUrl={currentUserAvatarUrl}
            userName={currentUserName}
            dataTestIdPrefix={dataTestIdPrefix}
            comment={comment}
            isExpanded={isExpanded}
            isPosting={isPosting}
            onFocus={handleFocus}
            onChange={setComment}
            onPost={handlePost}
            onCancel={handleCancel}
          />
        </div>
        {comments.length > 0 && (
          <ListComment
            comments={comments}
            dataTestIdPrefix={dataTestIdPrefix}
            onEdit={setEditingComment}
            onDelete={setCommentToDelete}
          />
        )}
        {editingComment && entityId && (
          <EditDataModal
            open={Boolean(editingComment)}
            title="Edit Comment"
            subtitle={getCommentSubtitle(editingComment)}
            onClose={() => setEditingComment(null)}
            onSuccess={handleEditSuccess}
          >
            <CommentEdit
              comment={editingComment}
              entityId={entityId}
              dataTestIdPrefix={dataTestIdPrefix}
              updateComment={updateComment}
            />
          </EditDataModal>
        )}
        <ConfirmModal
          open={Boolean(commentToDelete)}
          title="Delete Comment?"
          subtitle={getCommentSubtitle(commentToDelete)}
          description="Are you sure you want to delete this comment?"
          confirmLabel="Delete"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setCommentToDelete(null)}
          loading={isDeleting}
        />
      </Content>
    </Container>
  );
};

export default CommentsSection;
